import { useEffect } from "react";
import { Alert, Button, Container, Stack } from "@mantine/core";
import { createFileRoute, Outlet, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { accountAccessOptions } from "@/features/auth/access";
import { AdminAccessDenied, canAdminister } from "@/features/admin-users/access";
import { adminDenialOptions, adminDenialKey, clearAdminData } from "@/features/admin-users/queries";
import { AccessError, AccessPending } from "@/features/auth/components/AccessStatus";
import { ApiError } from "@/lib/apiClient";

function AdminDenied() {
  return <Container py="xl"><Stack><Alert title="Administrator access required">Your current account does not have access to administration.</Alert><Button variant="default" renderRoot={(props) => <Link {...props} to="/trips" />}>Back to trips</Button></Stack></Container>;
}
function AdminError({ error }: { error: unknown }) {
  return error instanceof AdminAccessDenied ? <AdminDenied /> : <AccessError error={error} />;
}
function AdminLayout() {
  const { session } = Route.useRouteContext();
  const cache = useQueryClient();
  const denial = useQuery(adminDenialOptions(session.user.id));
  const account = useQuery({ ...accountAccessOptions(session.user.id), refetchOnMount: false });
  const confirmedRevocation = Boolean(account.data && !canAdminister(account.data, session.user.id)) || (account.error instanceof ApiError && [401, 403].includes(account.error.status));
  useEffect(() => { if (confirmedRevocation) void clearAdminData(cache, session.user.id); }, [cache, session.user.id, confirmedRevocation]);
  if (denial.data) return <Container py="xl"><Stack><Alert title="Administrator access could not be confirmed">Administration has been closed and its data cleared.</Alert><Button loading={account.isFetching} disabled={account.isFetching} onClick={async () => {
    try { const current = await cache.fetchQuery(accountAccessOptions(session.user.id)); if (canAdminister(current, session.user.id)) cache.setQueryData(adminDenialKey(session.user.id), null); }
    catch { /* Keep the denial until an explicit successful access recheck. */ }
  }}>Recheck access</Button></Stack></Container>;
  if (account.isPending) return <AccessPending />;
  if (account.error) return <AccessError error={account.error} />;
  if (!canAdminister(account.data, session.user.id)) return <AdminDenied />;
  return <Outlet />;
}
export const Route = createFileRoute("/_authenticated/_protected/_admin")({
  beforeLoad: async ({ context }) => {
    const account = await context.queryClient.fetchQuery(accountAccessOptions(context.session.user.id));
    if (!canAdminister(account, context.session.user.id)) {
      await clearAdminData(context.queryClient, context.session.user.id);
      throw new AdminAccessDenied();
    }
  },
  component: AdminLayout, errorComponent: AdminError, pendingComponent: AccessPending,
});
